import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useGridApiContext } from '@mui/x-data-grid';

import { formatPriceConditionValue } from '@luxuryescapes/contract-svc-promo/channelMarkup/admin/v2';

import {
  type PriceConditionParts,
  type PriceConditionType,
  buildPriceConditionValue,
  isRangePriceConditionMode,
  parsePriceConditionValue,
} from './priceConditionValue';

// Friendlier names for the contract's rule prefixes; unknown rules fall back to the prefix itself.
const PRICE_CONDITION_MODE_LABELS: Record<string, string> = {
  'INSIDE-RANGE': 'Between (inclusive)',
  'OUTSIDE-RANGE': 'Not between',
  'HIGHER-THAN': 'Above',
  'HIGHER-THAN-OR-EQUAL': 'At or above',
  'LOWER-THAN': 'Below',
  'LOWER-THAN-OR-EQUAL': 'At or below',
};

type ChannelMarkupPriceConditionValueEditCellProps = {
  id: string | number;
  field: string;
  value?: unknown;
  conditionType: PriceConditionType;
  /** The contract's price rules, e.g. `INSIDE-RANGE` and `LOWER-THAN`. */
  modes: ReadonlyArray<string>;
};

export default function ChannelMarkupPriceConditionValueEditCell({
  id,
  field,
  value,
  conditionType,
  modes,
}: ChannelMarkupPriceConditionValueEditCellProps) {
  const apiRef = useGridApiContext();
  const storedValue = typeof value === 'string' ? value : String(value ?? '');
  const [parts, setParts] = useState<PriceConditionParts>(() => parsePriceConditionValue(storedValue, modes));
  const isRange = useMemo(() => isRangePriceConditionMode(parts.mode, conditionType), [conditionType, parts.mode]);
  const conditionValue = buildPriceConditionValue(parts, isRange);
  const preview = conditionValue ? formatPriceConditionValue(conditionValue) : null;

  // Re-read the stored value when it changes from outside this cell, keeping in-progress edits otherwise.
  useEffect(() => {
    setParts((currentParts) =>
      buildPriceConditionValue(currentParts, isRangePriceConditionMode(currentParts.mode, conditionType)) ===
      storedValue
        ? currentParts
        : parsePriceConditionValue(storedValue, modes),
    );
  }, [conditionType, modes, storedValue]);

  const updateParts = useCallback(
    (nextParts: Partial<PriceConditionParts>) => {
      const mergedParts = { ...parts, ...nextParts };
      setParts(mergedParts);
      void apiRef.current.setEditCellValue({
        id,
        field,
        value: buildPriceConditionValue(mergedParts, isRangePriceConditionMode(mergedParts.mode, conditionType)),
      });
    },
    [apiRef, conditionType, field, id, parts],
  );

  return (
    <Stack spacing={1} sx={{ width: '100%', py: 1 }} onKeyDown={(event) => event.stopPropagation()}>
      <TextField
        select
        size="small"
        label="Rule"
        value={parts.mode}
        onChange={(event) => updateParts({ mode: event.target.value })}
        fullWidth
      >
        {modes.map((mode) => (
          <MenuItem key={mode} value={mode}>
            {PRICE_CONDITION_MODE_LABELS[mode] ?? mode}
          </MenuItem>
        ))}
      </TextField>
      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          type="number"
          label={isRange ? 'Min price' : 'Price'}
          value={parts.min}
          onChange={(event) => updateParts({ min: event.target.value })}
          slotProps={{ htmlInput: { min: 0 } }}
          required
          fullWidth
        />
        {isRange && (
          <TextField
            size="small"
            type="number"
            label="Max price"
            value={parts.max}
            onChange={(event) => updateParts({ max: event.target.value })}
            slotProps={{ htmlInput: { min: 0 } }}
            required
            fullWidth
          />
        )}
      </Stack>
      <Typography variant="caption" color={conditionValue && !preview ? 'error' : 'text.secondary'}>
        {preview
          ? `Matches prices ${preview}`
          : conditionValue
            ? isRange
              ? 'Enter a min and a max, with the min no higher than the max.'
              : 'Enter a valid price.'
            : 'Enter a price.'}
      </Typography>
    </Stack>
  );
}
